import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

/**
 * Route-level error boundary for the authenticated layout.
 *
 * - Catches render errors thrown by a lazy route and shows a recoverable
 *   fallback ("Something went wrong — Retry / Back to dashboard").
 * - Resets automatically when the route changes (keyed by pathname), so
 *   navigating away clears a stuck error.
 * - Handles dynamic-import chunk load failures (stale bundle after a deploy)
 *   by reloading once. A sessionStorage flag guards against reload loops.
 *   NOTE: this flag is a UX guard, not an auth token — safe to store here.
 */

const CHUNK_RELOAD_FLAG = 'tg-platform-chunk-reload';

function isChunkLoadError(error: Error): boolean {
    const message = `${error.name} ${error.message}`;
    return (
        /ChunkLoadError/i.test(message) ||
        /Loading chunk [\d]+ failed/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message) ||
        /error loading dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message)
    );
}

interface BoundaryProps {
    children: ReactNode;
    onRetry: () => void;
    onBackToDashboard: () => void;
    labels: {
        title: string;
        description: string;
        retry: string;
        back: string;
    };
}

interface BoundaryState {
    hasError: boolean;
}

class ErrorBoundary extends Component<BoundaryProps, BoundaryState> {
    constructor(props: BoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): BoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, _info: ErrorInfo): void {
        // Stale-bundle recovery: one-shot reload, loop-guarded.
        if (isChunkLoadError(error)) {
            const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1';
            if (!alreadyReloaded) {
                sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
                window.location.reload();
            }
        }
    }

    private handleRetry = (): void => {
        this.setState({ hasError: false });
        this.props.onRetry();
    };

    private handleBack = (): void => {
        this.setState({ hasError: false });
        this.props.onBackToDashboard();
    };

    render(): ReactNode {
        if (!this.state.hasError) return this.props.children;
        const { labels } = this.props;
        return (
            <Card>
                <div className="flex flex-col items-center gap-3 p-6 text-center text-tg-ink">
                    <h1 className="text-lg font-semibold">{labels.title}</h1>
                    <p className="max-w-md text-sm text-tg-muted">{labels.description}</p>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                        <Button variant="default" onClick={this.handleRetry}>
                            {labels.retry}
                        </Button>
                        <Button variant="outline" onClick={this.handleBack}>
                            {labels.back}
                        </Button>
                    </div>
                </div>
            </Card>
        );
    }
}

/**
 * Function wrapper that supplies i18n labels + navigation callbacks and
 * remounts the class boundary on every route change (via the `key`), which
 * is what clears the error state when the user navigates elsewhere.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // Clear the chunk-reload guard on a successful mount so a *future*
    // genuine stale-bundle error is allowed to reload again.
    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1') {
        sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    }

    return (
        <ErrorBoundary
            key={location.pathname}
            onRetry={() => navigate(0)}
            onBackToDashboard={() => navigate('/dashboard')}
            labels={{
                title: t('errors.boundary.title', { defaultValue: 'Something went wrong' }),
                description: t('errors.boundary.description', {
                    defaultValue: 'This page failed to load. Try again or return to the dashboard.',
                }),
                retry: t('errors.boundary.retry', { defaultValue: 'Retry' }),
                back: t('errors.boundary.back', { defaultValue: 'Back to dashboard' }),
            }}
        >
            {children}
        </ErrorBoundary>
    );
}
